define('bindingHandlers/combobox',[
    'jquery',
    'lib/bluebird',
    'lib/lodash4',
    'lib/knockout',
    'util/keyCode',
    'services/loggingService',
    //no references required
    'bindingHandlers/tooltip',
    'extensions/i18n/knockout.i18n',
    'lib/select2/select2'
], function (
    $,
    Promise,
    _,
    ko,
    keyCode,
    loggingService
) {
    'use strict';

    var createTag = function (string) {
        return { id: string, name: string };
    };

    /**
     * A binding handler for displaying a combobox widget.   The combobox supports dynamic loading of data that
     * can come from an external source.
     *
     * # Required Arguments
     *
     * @param {Object} value - An observable that will be updated with the current selection of the combobox.
     * @param {Function} search - A function that will be called when new options need to be displayed in the control.
     *    The function should return a promise that resolves to a list of objects that will be displayed as selectable
     *    options.  The function should accept a string parameter that is the current search term that has been entered
     *    by the user.
     *
     * # Optional Arguments
     *
     * @param {Boolean} allowClear - Whether or not the user can clear the current selection.  Defaults to false. Requires placeholder.
     * @param {Boolean} multiple - Enable multiple selection. Defaults to false.
     * @param {Boolean} sortable - Enable sorting multi-selects. Requires the multiple parameter to be true.  Defaults to false.
     * @param {Boolean} autoDrop - Enable automatically opening select2 on creation. Defaults to false.
     * @param {Boolean} [tagMode=false]  - Converts the combobox to tag mode by hiding the dropdown, turning on support for multiple, and adding a function for createSearchChoice (createTag).
     * @param {String|Object} placeholder - Text to show when no selection has been made.  Defaults to null (no placeholder)
     *                                      - Can also be passed a localizable string key, or an object representing { key: localizable key, data: {} } to render as string data
     * @param {String} text - The property on the object that should be displayed in the control. Defaults to 'name'.
     * @param {String} id - The property on the object that should be used for identification.  Defaults to 'id'.
     * @param {Object} {Array} fixedOptions - Will create a set of clickable icon/name pairs fixed at the bottom of the combo dropdown results.  May be one object or an array of objects.  Requires a 'name' and 'click' properties on each object.  The 'iconClass' property is optional and defaults to 'fa-plus-circle'.  iconClass may be set to null to indicate no leading icon should be used.
     * @param {Boolean|Object} tooltipOptions - Defaults to undefined. If truthy, will show tooltips on selected comboboxitems. Supports the following properties:
     *                                          - tooltipText: The property on the object that should be displayed on the tooltip. Defaults to 'name'.
     *                                          - displayOnTruncateOnly: true/false value passed into tooltip binding handler. Defaults to true.
     * @param {Boolean} disabled - Will disable the combobox when truthy
     * @param {Boolean} disableSearchDelay - Defaults to false. Allows the search function to run on every keystroke when set to true.
     * @param {Function} onOpen - A method to run on open.
     * @param {Function} onEnter - A method to run on enter.
     * @param {Function} onClose - A method to run on close.
     * @param {Function} dontSearchOnEmptySelection - Will make sure an empty search entry (say a focus or click into the control) does not result in calling search resulting in dropdown showing matches.
     *
     * # Examples
     *
     *   <input class="form-control" type="hidden" data-bind="combobox: {
     *        search: performSearch,
     *        value: myValueObservable,
     *        fixedOptions: {
     *            name: 'my-localization-key',
     *            click: doSomethingAmazing
     *        }
     *   }">
     *
     *   <input class="form-control" type="hidden" data-bind="combobox: {
     *        search: performSearch,
     *        value: myOtherValueObservable,
     *        fixedOptions: [
     *            {
     *                name: 'my-first-key',
     *                click: harvestAwesome,
     *                iconClass: null
     *            },
     *            {
     *                name: 'my-second-key',
     *                click: doSomethingAmazing,
     *                iconClass: 'fa-plus-circle'
     *            }
     *        ]
     *   }">
     *
     *
     *   self.performSearch = function (searchTerm) {
     *      return myService.search(searchTerm);
     *   };
     *
     *   self.doSomethingAmazing = function () {
     *       self.amazingHasHappened = true;
     *   };
     *
     *  Validation note: Select2 gives the input an initial value of null, so if you
     *    create an obserable with no value (undefined), a change event will trigger, and
     *    the combobox will be immediately validated. To prevent this, create your observable
     *    with an initial value of null:
     *        self.myObs = ko.observable().extend({required: true}); - causes instant validation
     *        self.myObs = ko.observable(null).extend({required: true}); - waits for change/submit to validate
     */
    ko.bindingHandlers.combobox = {
        init: function(element, valueAccessor) {
            var $element = $(element);
            var options = ko.unwrap(valueAccessor());
            if (!$element.is('input') && !$element.is(':hidden')) {
                throw new Error('combobox binding is only allowed on hidden input elements.');
            }

            if(options.sortable && !options.multiple){
                throw new Error('combobox binding only allows sortable with multiple declaration.');
            }

            if(options.allowClear && !options.placeholder) {
                loggingService.warn("combobox allowClear option requires a placeholder to be set");
            }

            if (_.isUndefined(options.search) && options.tagMode) {
                options.search = function () {
                    return Promise.resolve([]);
                };
            }

            var getProperty = function(option, defaultOptions) {
                return function(item) {
                    var property = item[options[option] || defaultOptions];
                    var data = ko.isObservable(property) ? property() : property;

                    if (data) {
                        data = _.escape(data);
                    }

                    return data;
                };
            };

            var getResultText = function(item) {
                var result = getProperty('text', 'name')(item);
                var infoText = getProperty('infoText', 'infoText')(item);
                if (infoText) {
                    result += '<div class="combobox-info-text"> ' + infoText + '</div>';
                }
                return result;
            };

            var formatSelection = function(item) {
                var text = getProperty('text', 'name')(item);
                if (options.tooltipOptions) {
                    var tooltipOptions = {
                        tooltipText: 'name',
                        displayOnTruncateOnly: true
                    };

                    if (_.isObject(options.tooltipOptions)) {
                        tooltipOptions = _.assign(tooltipOptions, options.tooltipOptions);
                    }

                    var tooltipText = ko.unwrap(item[tooltipOptions.tooltipText]) || '';
                    var displayOnTruncateOnly = tooltipOptions.displayOnTruncateOnly;
                    if (!_.hasValue(text)) {
                        text = '';  // Don't show 'undefined' as a literal here, as the non-tooltipOptions path above wont
                    }

                    // Problems happen when the tooltipText contains single quote.  \ The single quote for knockout, then
                    // html escape the whole value so it renders correctly in the DOM.
                    return '<div class="combobox-item" data-bind="tooltip: { text: \'' + _.escape(tooltipText.replace('\'', '\\\'')) + '\', displayOnTruncateOnly: ' + displayOnTruncateOnly + '}">' + text + '</div>';
                }
                return text;
            };

            // wrapping the query in a delay so that it only queries once the
            // user is done typing (timeout of 200ms)
            var getQuery = function () {
                var timeout;
                return function (query) {
                    window.clearTimeout(timeout);
                    var searchDelayMs = options.disableSearchDelay ? 0 : 200;
                    if (options.searchDelayMs !== undefined) {
                        searchDelayMs = options.searchDelayMs;
                    }
                    timeout = window.setTimeout(function() {
                        options.search(query.term)
                            .then(function (results) {
                                query.callback({ results: results });
                            })
                            .catch(function () {
                                query.callback({ results: [] });
                            });
                    }, searchDelayMs);
                };
            };

            var placeholderString = getPlaceholderString(options);

            // Initialize the control
            $element.select2({
                id: getProperty('id', 'id'),
                allowClear: options.allowClear || false,
                containerCssClass: options.tagMode ? 'combobox-tag-mode' : null,
                dropdownCssClass: options.tagMode ? 'combobox-hide-dropdown' : '',
                multiple: options.multiple || options.tagMode || false,
                placeholder: placeholderString,
                createSearchChoice: options.createSearchChoice || (options.tagMode ? createTag : null),
                formatSearching: function () { return ko.i18n.renderString('widget.combobox.searching'); },
                formatNoMatches: function() { return ko.i18n.renderString('widget.combobox.noMatches'); },
                formatSelection: options.formatSelection || formatSelection,
                formatResult: options.formatResults || getResultText,
                query: getQuery(),
                closeSelectionLabel: ko.i18n.renderString('widget.combobox.removeSelection')
            });

            function autoDropSelect(value) {
                if (value) {
                    $element.select2("open");
                }
            }

            var autoDrop = options.autoDrop,
                autoDropIsObservable = ko.isObservable(autoDrop),
                autoDropSubscription;

            if (autoDropIsObservable) {
                autoDropSubscription = autoDrop.subscribe(autoDropSelect);
            } else {
                autoDropSelect(autoDrop);
            }

            var inputVal = null;

            function handleChange(e) {

                // if a value was selected, cache it so that when the
                // handleOpening callback fires, we know to prevent the default.
                if (!_.isUndefined(e.val) &&
                    !_.isEmpty(e.val) &&
                    _.isFunction(options.onEnter)) {
                        inputVal = e.val;
                }

                e.stopImmediatePropagation();

                options.value($element.data().select2.data());

                // Apply bindings to items. Could be useful to do it always, but just needed for tooltips for now.
                if (options.tooltipOptions) {
                    var comboboxItems = options.multiple ?
                        $element.prev().find('.select2-search-choice') :
                        $element.prev().find('.select2-chosen');
                    for (var i=0; i < comboboxItems.length; i++) {
                        var alreadyApplied = ko.utils.domData.get(comboboxItems[i], 'appliedBindings');
                        var hasComboboxItem = $(comboboxItems[i]).find('.combobox-item').length;
                        if (!alreadyApplied && hasComboboxItem) {
                            ko.applyBindings({}, comboboxItems[i]);
                            ko.utils.domData.set(comboboxItems[i], 'appliedBindings', true);
                        }
                    }
                }
            }

            // listen for changes and update the value binding
            $element.on('change', handleChange);

            // if onEnter method was provided, bind the keydown event.
            if(_.isFunction(options.onEnter)) {

                $element.prev().find('input.select2-offscreen').on('keydown', function(e) {
                    if (e.keyCode === keyCode.ENTER) {
                        options.onEnter();
                    }
                });

                $element.on("select2-opening", function (e) {

                    var userSearchInput = $element.prev().find('input.select2-input').val();
                    if (options.dontSearchOnEmptySelection && (_.isNull(userSearchInput) || _.isEmpty(userSearchInput))) {
                        e.preventDefault();
                        inputVal = null;
                        return;
                    }

                    if (!_.isNull(inputVal)) {
                        e.preventDefault();
                        inputVal = null;
                    }
                });

                // Set cache value to null when the focus is lost so that
                // control is opened upon next click or keydown enter event
                $element.on("select2-blur", function() {
                    inputVal = null;
                });
            }

            $element.on("select2-open", function () {
                if(_.isFunction(options.onOpen)) {
                    options.onOpen();
                }

                if (options.fixedOptions) {
                    var activeDropDown = $('#select2-drop');

                    //the binding can fire multiple times, ensure only append once
                    if (activeDropDown.find('.combobox-fixed-options-container').length === 0) {
                        var fixedOptions = options.fixedOptions;

                        //support single-object config - simply place object into an array for consistency
                        if (!_.isArray(fixedOptions)) {
                            fixedOptions = [fixedOptions];
                        }

                        //create a fragment to work on until the final append
                        var domFixedOptionsContainer = $('<div class="combobox-fixed-options-container"></div>');

                        _.each(fixedOptions, function (fixedOption) {
                            if (_.isUndefined(fixedOption.name)) {
                                throw new Error("The fixedOptions property used in the combobox binding requires a 'name' property for localized text");
                            }

                            if (_.isUndefined(fixedOption.click)) {
                                throw new Error("The fixedOptions property used in the combobox binding requires a 'click' property bound to an observable");
                            }

                            //null opts for no class and undefined signals the default icon
                            var iconClass = '';

                            if (_.isString(fixedOption.iconClass)) {
                                iconClass = fixedOption.iconClass;
                            } else if(_.isUndefined(fixedOption.iconClass)) {
                                iconClass = 'fa fa-plus-circle';
                            }

                            var name = ko.i18n.renderString(fixedOption.name);

                            var domFixedOption = '' +
                                '<div class="btn combobox-fixed-option">' +
                                '<i class="' + iconClass + '"> </i> ' +
                                '<span>' + name + '</span>' +
                                '</div>';

                            $(domFixedOption)
                                .click(function () {
                                    $element.select2('close');
                                    fixedOption.click(element, 'fixedOption');
                                })
                                .appendTo(domFixedOptionsContainer);
                        });

                        //now that all options are accounted for, append the lead fragment to the active dom
                        domFixedOptionsContainer.appendTo(activeDropDown);
                    }
                }
            });

            $element.on("select2-close", function () {
                if(_.isFunction(options.onClose)) {
                    options.onClose();
                }
            });

            //select2 has built-in support for sortable multi-selects
            if(options.sortable) {
                $element.select2('container').find('ul.select2-choices').sortable({
                    containment: 'parent',
                    start: function () {
                        $element.select2("onSortStart");
                    },
                    update: function () {
                        $element.select2("onSortEnd");
                    }
                });
            }


            ko.utils.domNodeDisposal.addDisposeCallback(element, function() {
                $element.off('change', handleChange);
                $element.select2('destroy');
                $element.off("select2-opening");
                $element.off('select2-close');

                if (autoDropSubscription) {
                    autoDropSubscription.dispose();
                }
            });
        },
        update: function(element, valueAccessor) {
            var $element = $(element);
            var options = ko.unwrap(valueAccessor());

            if (_.isUndefined(options.value)) {
                throw new Error("combobox binding requires a reference to an observable or an object with a 'value' attribute as its argument.");
            }

            if (_.isUndefined(options.search)) {
                if (options.tagMode) {
                    options.search = function () { return Promise.resolve([]); };
                } else {
                    throw new Error("combobox binding requires a reference to an observable or an object with a 'search' attribute as its argument.");
                }
            }

            var existingValue = $element.data().select2.data();
            var newValue = ko.utils.unwrapObservable(options.value);
            if (existingValue !== newValue) {
                $element.data().select2.data(newValue);
                $element.trigger('change');
            }

            if (ko.unwrap(options.disabled)) {
                $element.select2('disable');
            } else {
                $element.select2('enable');
            }

            var placeholderString = getPlaceholderString(options);

            if (placeholderString && $element.data().select2.setPlaceholder) {
                $element.attr('data-placeholder', placeholderString);
                $element.data().select2.setPlaceholder();
            }
        }
    };

    var getPlaceholderString = function (options) {
        var placeholder = ko.unwrap(options.placeholder);
        if (_.isObject(placeholder)) {
            return ko.i18n.renderString(placeholder.key, placeholder.data);
        } else if (ko.i18n.keyExists(placeholder)) {
            return ko.i18n.renderString(placeholder);
        } else {
            return placeholder;
        }
    };
});

